import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Sparkles, ShieldCheck, X, RotateCw } from "lucide-react";

/**
 * "Returning customer?" banner + OTP-gated address recall.
 * On success, calls `onRecalled(formObj)` with the saved address so the parent
 * can prefill its Checkout form state.
 *
 * Privacy: we send an OTP to the phone (and email if on file) before revealing
 * the address, so no one can sniff a stranger's saved address by typing their
 * number.
 */
const CheckoutRecall = ({ onRecalled }) => {
  const [phone, setPhone] = useState("");
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("phone"); // 'phone' | 'otp'
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const dismissedRef = useRef(localStorage.getItem("rieave.checkout.recall.dismissed") === "1");
  const [hidden, setHidden] = useState(dismissedRef.current);

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const startRecall = async () => {
    const cleaned = phone.replace(/\s/g, "");
    if (!/^\+?\d{10,15}$/.test(cleaned)) {
      toast.error("Enter a valid phone number to look up your saved address.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/recall-request-otp", { phone: cleaned });
      if (!data.exists) {
        toast("We don't have a saved address for this number — please fill the form.");
        setOpen(false);
        return;
      }
      setStage("otp");
      setCooldown(30);
      setOpen(true);
      toast.success("OTP sent — check your phone (and email).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start recall");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (otp) => {
    const cleaned = phone.replace(/\s/g, "");
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/recall-verify-otp", { phone: cleaned, code: otp });
      toast.success("Welcome back ✨ — address filled in.");
      onRecalled(data);
      setOpen(false);
      setStage("phone");
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP didn't match");
    } finally {
      setLoading(false);
    }
  };

  // Auto-verify when OTP slot is fully entered
  useEffect(() => {
    if (stage === "otp" && code.length === 6 && !loading) {
      verify(code);
    }
    // eslint-disable-next-line
  }, [code]);

  const dismiss = () => {
    localStorage.setItem("rieave.checkout.recall.dismissed", "1");
    setHidden(true);
  };

  if (hidden) return null;

  return (
    <>
      <section
        className="bg-gradient-to-br from-lavender/40 via-blush/30 to-lavender/40 border border-rose/20 rounded-2xl p-5 mb-2"
        data-testid="checkout-recall-banner"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-rose/15 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles size={16} className="text-rose" />
            </div>
            <div>
              <p className="font-cormorant text-lg text-charcoal leading-tight">
                Ordered with us before?
              </p>
              <p className="text-xs text-mauve mt-0.5 flex items-center gap-1.5">
                <ShieldCheck size={11} /> OTP-verified address recall — no need to retype.
              </p>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="text-charcoal/40 hover:text-charcoal p-1"
            aria-label="dismiss"
            data-testid="recall-banner-dismiss"
          >
            <X size={14} />
          </button>
        </div>
        <div className="flex gap-2">
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Your phone (e.g. 9876543210)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="h-11 rounded-xl bg-white"
            data-testid="recall-phone-input"
          />
          <button
            onClick={startRecall}
            disabled={loading || !phone}
            className="h-11 px-5 rounded-xl bg-charcoal text-cream font-body text-sm disabled:opacity-50 whitespace-nowrap"
            data-testid="recall-send-otp-button"
          >
            {loading ? "Checking…" : "Send OTP"}
          </button>
        </div>
      </section>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) {
            setOpen(false);
            setStage("phone");
            setCode("");
          }
        }}
      >
        <DialogContent className="max-w-md bg-cream" data-testid="recall-otp-dialog">
          <DialogHeader>
            <DialogTitle className="font-hero text-2xl">Enter your code</DialogTitle>
            <DialogDescription>
              We sent a 6-digit code to your phone {/* and email */} ending in{" "}
              <strong>{phone.slice(-4)}</strong>. It expires in 5 minutes.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-3">
            <InputOTP
              value={code}
              onChange={setCode}
              maxLength={6}
              disabled={loading}
              data-testid="recall-otp-input"
            >
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <InputOTPSlot key={i} index={i} />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={startRecall}
              disabled={cooldown > 0 || loading}
              className="text-sm text-rose hover:underline disabled:opacity-50 disabled:no-underline inline-flex items-center gap-1.5"
              data-testid="recall-resend-otp"
            >
              <RotateCw size={12} /> Resend {cooldown > 0 ? `in ${cooldown}s` : ""}
            </button>
            <p className="text-xs text-mauve">{loading ? "Verifying…" : "Auto-submits when complete"}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CheckoutRecall;
